import { useEffect, useMemo, useState } from "react";
import {
    AnalyticsChartCard,
    MultiLineChart,
    StyledPieChart,
} from "../ChartComponents";
import DashboardMetricsService from "../../services/dashboardMetricsService";
import { useDashboardUI } from "../../context/GlobalContext";
import { buildAnalyticsQueryParams } from "../../helpers/helpers";
import GenericAnalytics from "./genericAnalytics";

/**
 * Merge multiple analytics arrays by date
 */
function mergeAnalyticsByDate(metrics) {
    const map = {};

    metrics.forEach(({ key, source, valueKey }) => {
        source.forEach(item => {
            if (!map[item.date]) {
                map[item.date] = { date: item.date };
            }
            map[item.date][key] = item[valueKey] || 0;
        });
    });

    return Object.values(map).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
    );
}

/**
 * Metric config for search usage
 */
const METRIC_CONFIG = {
    web_search_usage: {
        metricKey: "web_search_usage",
        chartKey: "web_search",
        valueKey: "total_count",
        label: "Web Search",
        color: "#6366F1",
    },
    intranet_search_usage: {
        metricKey: "intranet_search_usage",
        chartKey: "intranet_search",
        valueKey: "total_count",
        label: "Intranet Search",
        color: "#22C55E",
    },
};

/**
 * Colors for model usage pie
 */
const MODEL_COLORS = [
    "#6366F1",
    "#22C55E",
    "#F59E0B",
    "#EF4444",
    "#0EA5E9",
    "#8B5CF6",
];

function transformModelUsageToPieData(models = []) {
    return models.map((item, index) => ({
        name: item.model,
        value: item.count || 0,
        color: MODEL_COLORS[index % MODEL_COLORS.length],
    }));
}

export default function PlatformUsage() {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const {
        selectedFacilityFilter,
        selectedStartDateFilter,
        selectedEndDateFilter,
        selectedClientFilter,
        setTotalActiveSessions,
    } = useDashboardUI();

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            setError(false);

            // client_app only narrows total_active_sessions server-side; the
            // search and model-usage series below stay facility-wide.
            const queryParams = buildAnalyticsQueryParams({
                startValue: selectedStartDateFilter,
                endValue: selectedEndDateFilter,
                facilityId: selectedFacilityFilter?.facility_number,
                extra: { client_app: selectedClientFilter },
            });

            const response =
                await DashboardMetricsService.getPlatformUsage(queryParams);

            setAnalytics(response?.data);
            setTotalActiveSessions(response?.data?.total_active_sessions);
        } catch (err) {
            console.error("Failed to fetch platform usage analytics", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, [selectedStartDateFilter, selectedEndDateFilter, selectedFacilityFilter, selectedClientFilter]);

    /**
     * Search usage line chart
     */
    const searchUsageChart = useMemo(() => {
        if (!analytics) return { chartData: [], lines: [] };

        const metrics = Object.values(METRIC_CONFIG).map(config => ({
            mergeConfig: {
                key: config.chartKey,
                source: analytics?.[config.metricKey] || [],
                valueKey: config.valueKey,
            },
            lineConfig: {
                dataKey: config.chartKey,
                stroke: config.color,
                name: config.label,
            },
        }));

        return {
            chartData: mergeAnalyticsByDate(
                metrics.map(m => m.mergeConfig)
            ),
            lines: metrics.map(m => m.lineConfig),
        };
    }, [analytics]);

    /**
     * Model usage pie
     */
    const modelUsagePieData = useMemo(() => {
        if (!analytics) return [];
        return transformModelUsageToPieData(analytics.models_usage);
    }, [analytics]);

    const totalModelUsage = modelUsagePieData.reduce(
        (sum, d) => sum + d.value,
        0
    );

    /**
     * Loading / error
     */
    if (loading) {
        return (
            <div className="rounded-xl border bg-white p-6 shadow-sm">
                <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
                    Loading analytics…
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-xl border bg-white p-6 shadow-sm">
                <div className="flex h-[300px] items-center justify-center text-sm text-red-500">
                    Failed to load analytics
                </div>
            </div>
        );
    }

    /**
     * UI
     */
    return (
        <div>
            {/* <GenericAnalytics /> */}
            <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
                {/* <div className="w-full rounded-xl border bg-white p-6 shadow-sm">
                    <div className="mb-4 text-sm font-medium text-slate-700">
                        Model Usage
                    </div>
                    {totalModelUsage > 0 ? (
                        <div className="flex flex-col">
                            <StyledPieChart
                                data={modelUsagePieData}
                                dataKey="value"
                                nameKey="name"
                                colors={modelUsagePieData.map(d => d.color)}
                            />

                            <div className="mt-6 text-sm font-medium text-slate-700 text-center">
                                Total Model Usage:{" "}
                                <span className="font-semibold text-slate-900">
                                    {totalModelUsage}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-[300px] text-gray-500">
                            No model usage data available
                        </div>
                    )}
                </div> */}

                <div className="min-w-0 w-full overflow-hidden rounded-2xl border border-[#ECEEF4] bg-white">
                    <div className="px-5 pb-4 pt-5 sm:px-6">
                        <h2 className="text-base font-bold tracking-tight text-[#14122B]">Model Activity By Output</h2>
                    </div>

                    {totalModelUsage > 0 ? (
                        <>
                            <div className="table-scroll overflow-auto max-h-[300px]">
                                <table className="w-full min-w-[360px] border-collapse text-sm">
                                    <thead className="sticky top-0 z-10">
                                        <tr className="border-y border-[#ECEEF4] bg-[#FAFAFD] text-[11px] font-semibold uppercase tracking-wide text-[#9AA1B2]">
                                            <th className="py-2.5 pl-5 pr-2 text-left sm:pl-6">Model</th>
                                            <th className="py-2.5 px-2 text-right">Usage</th>
                                            <th className="py-2.5 pl-2 pr-5 text-right sm:pr-6">%</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {modelUsagePieData.map((item) => {
                                            const percentage = totalModelUsage
                                                ? Math.round((item.value / totalModelUsage) * 100)
                                                : 0;

                                            return (
                                                <tr key={item.name} className="border-b border-[#F1F2F7] last:border-b-0 hover:bg-gray-50 transition-colors">
                                                    <td className="max-w-0 py-3 pl-5 pr-2 align-middle sm:pl-6">
                                                        <div className="flex items-center gap-2.5">
                                                            <span
                                                                className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                                                                style={{ backgroundColor: item.color }}
                                                            />
                                                            <span className="truncate text-sm font-bold text-[#14122B]" title={item.name}>{item.name}</span>
                                                        </div>
                                                    </td>

                                                    <td className="whitespace-nowrap py-3 px-2 text-right align-middle text-sm font-semibold text-[#14122B]">
                                                        {item.value}
                                                    </td>

                                                    <td className="whitespace-nowrap py-3 pl-2 pr-5 text-right align-middle text-sm text-[#4B5266] sm:pr-6">
                                                        {percentage}%
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="border-t border-[#ECEEF4] bg-[#FAFAFD] px-5 py-3 text-right text-sm text-[#9AA1B2] sm:px-6">
                                Total Model Usage:{" "}
                                <span className="font-bold text-[#14122B]">
                                    {totalModelUsage}
                                </span>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center h-[300px] text-gray-500">
                            No model usage data available
                        </div>
                    )}
                </div>

                <AnalyticsChartCard
                    title="Search Usage Over Time"
                    subtitle=""
                    {...searchUsageChart}
                />

            </div>
        </div>
    );
}
